/**
 * `JasenedutBenefitCard` — one `/jasenedut` benefit entry (029 US3 / T031).
 *
 * Extracted from `JasenedutRoute` so the route stays under the 400-line
 * architecture ceiling while this file owns the per-entry presentation to
 * proto parity (the proto's `Jasenedut.tsx`):
 *
 *   - a 96x80 partner logo rendered `object-contain` (so a wide or tall logo
 *     fits the box without distortion). When `logoUrl` is `null` — every
 *     seeded demo benefit, since the demo benefits are fabricated and carry no
 *     real-partner asset — a muted placeholder box renders in its place (NO
 *     `<img>`), the honest "no logo" state.
 *   - the entry title as an `<h3>`.
 *   - a collapsible "Näytä lisätiedot" disclosure whose body is the authored
 *     HTML `description` rendered through `SanitizedHtml` (FR-049 — never
 *     `dangerouslySetInnerHTML`). The body stays MOUNTED when collapsed (hidden
 *     via the `hidden` attribute) so it is always sanitised in place.
 *   - an external `link` as a real anchor ("Lue lisää täältä") opening a new
 *     tab (`target="_blank" rel="noopener noreferrer"`), rendered ONLY when the
 *     benefit carries a non-null, safe http(s) `link` (`isSafeHttpUrl`, review
 *     finding: an authored javascript:/data: URL never reaches the href sink). An
 *     `sr-only` cue announces the new tab to assistive tech (R-US3-4, carrying
 *     the US2 new-tab lesson).
 */

import { type JSX, useId, useState } from 'react';

import { isVideoUrl } from '../../components/cms/isVideoUrl.js';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { ChevronDownIcon } from '../../shell/icons.js';
import type { Benefit } from '../ContentReaderPort.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';
import { isSafeHttpUrl } from '../safeUrl.js';

const cardClasses = 'relative rounded-lg border border-border bg-card p-4';
const topRowClasses = 'mb-3 flex items-center gap-4';
const logoImageClasses = 'h-20 w-24 flex-shrink-0 object-contain';
const logoPlaceholderClasses = 'h-20 w-24 flex-shrink-0 rounded bg-muted';
const titleClasses = 'flex-1 text-base font-semibold text-foreground';
const disclosureButtonClasses =
  'flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const disclosureChevronClasses = 'h-4 w-4 flex-shrink-0 transition-transform';
const descriptionClasses =
  'mt-3 text-sm text-muted-foreground [&_a]:text-tab-active [&_a]:underline';
const linkClasses = 'mt-3 inline-flex self-start text-sm font-medium text-tab-active underline';

export function JasenedutBenefitCard({
  benefit,
  t,
}: {
  readonly benefit: Benefit;
  readonly t: (key: MessageKeys) => string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const descriptionId = useId();

  return (
    <article className={cardClasses}>
      <div className={topRowClasses}>
        {benefit.logoUrl !== null ? (
          // 024-cms Phase U: the benefit logo is IMAGE-ONLY per D-148 (BenefitForm
          // sets `accept="image"`), so the `<video>` branch is DEAD today. It is
          // pre-wired only so flipping the field's accept flag to image+video is a
          // one-line change. A logo is DECORATIVE, so the video branch matches the
          // HomeCards / LakiruutuVideoCard poster pattern: muted, non-focusable
          // (`tabIndex={-1}`), `aria-hidden`, and NO `controls`.
          isVideoUrl(benefit.logoUrl) ? (
            <video
              src={benefit.logoUrl}
              className={logoImageClasses}
              muted
              tabIndex={-1}
              aria-hidden="true"
            />
          ) : (
            <img src={benefit.logoUrl} alt="" className={logoImageClasses} />
          )
        ) : (
          <span aria-hidden="true" className={logoPlaceholderClasses} />
        )}
        <h3 className={titleClasses}>{benefit.title}</h3>
      </div>

      <button
        type="button"
        className={disclosureButtonClasses}
        aria-expanded={open}
        aria-controls={descriptionId}
        // Per-card accessible name: the visible "Näytä lisätiedot" plus the
        // entry title, so the N disclosure toggles are distinguishable by AT.
        aria-label={`${t('jasenedut.showDetails')}: ${benefit.title}`}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <span>{t('jasenedut.showDetails')}</span>
        <ChevronDownIcon className={`${disclosureChevronClasses} ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* The description stays mounted when collapsed (hidden attribute) so the
          authored HTML is always sanitised in place and the disclosure is a pure
          show/hide. `SanitizedHtml` strips script + event handlers (FR-049). */}
      <SanitizedHtml
        as="div"
        id={descriptionId}
        hidden={!open}
        className={descriptionClasses}
        html={benefit.description}
      />

      {benefit.link !== null && isSafeHttpUrl(benefit.link) ? (
        // Real anchor (not window.open): a benefit's offer URL is a navigable
        // destination. New tab with no opener handle / no referrer. The
        // `isSafeHttpUrl` gate (review finding) keeps an authored javascript:/data:
        // URL out of the href navigation sink, mirroring the SanitizedHtml
        // read-side posture; an unsafe link renders no anchor.
        <a className={linkClasses} href={benefit.link} target="_blank" rel="noopener noreferrer">
          {t('jasenedut.linkLabel')}
          <span className="sr-only"> {t('jasenedut.linkNewTabHint')}</span>
        </a>
      ) : null}
    </article>
  );
}
